import { Injectable } from '@angular/core';
import { AuthMode } from './auth-mode.enum';
import { Eventsetup } from './eventsetup';

/**
 * Zentraler Service zur Verwaltung des Authentifizierungsmodus eines Events.
 * Speichert den AuthMode und das Event-Token, damit AuthGuard
 * und LoginComponent auf denselben Zustand zugreifen können.
 */
@Injectable({
  providedIn: 'root'
})
export class EventAuthService {

  private authMode: AuthMode | null = null;
  private eventToken: string | null = null;
  private userMode: 'int' | 'ext' = 'ext';

  // ── Getter / Setter ──────────────────────────────────────────────

  setAuthMode(mode: AuthMode): void {
    this.authMode = mode;
  }

  getAuthMode(): AuthMode | null {
    return this.authMode;
  }


  setUserMode(mode: 'int' | 'ext'): void {
    this.userMode = mode;
  }

  getUserMode(): 'int' | 'ext' {
    return this.userMode;
  }

  isInitialized(): boolean {
    return this.authMode !== null;
  }

  // ── Auth-Modus aus Eventsetup lesen ──────────────────────────────

  /**
   * Ermittelt den Auth-Modus für einen Netzwerk-Scope aus config_setup.json.
   *
   * NUR NOCH FALLBACK: maßgeblich ist das Event-Dokument, das die LoginComponent über
   * AuthService.getAuthConfig() lädt. Diese Methode greift, wenn es dafür (noch) kein
   * Dokument gibt — also solange nicht alle Events nach Customer_{slug}.events
   * migriert sind.
   *
   * `authMode` legt den Mechanismus fest, `authEnabled{Int,Ext}` legt fest, ob dieser
   * Mechanismus für interne bzw. externe Nutzer überhaupt greift. Ist er für den Scope
   * abgeschaltet, gilt dort NONE — also kein Login.
   *
   *  'none'           → AuthMode.NONE
   *  'email_token'    → AuthMode.EMAIL_TOKEN
   *  'email_password' → AuthMode.EMAIL_PASSWORD
   *  'media_library'  → AuthMode.MEDIA_LIBRARY
   */
  resolveAuthModeFor(setup: Eventsetup, scope: 'int' | 'ext'): AuthMode {
    const mode = this.toAuthMode(setup?.authMode);

    // MEDIA_LIBRARY läuft über einen Token aus der Mediathek und kennt kein int/ext.
    if (mode === AuthMode.NONE || mode === AuthMode.MEDIA_LIBRARY) {
      return mode;
    }

    const enabled = scope === 'int' ? setup?.authEnabledInt : setup?.authEnabledExt;

    // Noch nicht migrierte Events kennen die Flags nicht. Dann greift das alte
    // Begriffspaar password_int/password_ext, das dieselbe Frage beantwortet hat.
    if (enabled === undefined || enabled === null) {
      const legacy = scope === 'int' ? setup?.password_int : setup?.password_ext;
      if (legacy === undefined || legacy === null) {
        // Auch kein Legacy-Wert: Modus gilt für beide Scopes (Verhalten vor den Flags).
        return mode;
      }
      return legacy ? mode : AuthMode.NONE;
    }

    return enabled ? mode : AuthMode.NONE;
  }

  /**
   * Erzeugt (oder liest) die Client-ID für die Zählung anonymer Zugriffe.
   *
   * Bewusst pro Event gescoped, damit daraus keine eventübergreifende Wiedererkennung
   * wird. Ist localStorage nicht verfügbar (Privatmodus, blockierte Speicherung), wird
   * null zurückgegeben und gar nicht gezählt — eine flüchtige ID würde die Zahlen bei
   * jedem Seitenaufruf aufblähen.
   */
  getOrCreateClientId(eventToken: string): string | null {
    const key = `lse-cid-${eventToken}`;
    try {
      const existing = localStorage.getItem(key);
      if (existing) return existing;

      const created = crypto.randomUUID();
      localStorage.setItem(key, created);
      return created;
    } catch {
      return null;
    }
  }

  /**
   * Übersetzt den Modus-String (aus dem Event-Dokument oder aus config_setup.json)
   * in den AuthMode-Enum.
   */
  toAuthMode(value: string | undefined | null): AuthMode {
    switch (value) {
      case 'none':
        return AuthMode.NONE;
      case 'email_token':
        return AuthMode.EMAIL_TOKEN;
      case 'email_password':
        return AuthMode.EMAIL_PASSWORD;
      case 'media_library':
        return AuthMode.MEDIA_LIBRARY;
      default:
        // Fallback: wenn kein authMode gesetzt ist, EMAIL_PASSWORD
        return AuthMode.EMAIL_PASSWORD;
    }
  }
}
